import { NpcFicheAside } from '@/components/admin/npc/launch/fiche-aside';
import { NpcEditor } from '@/components/admin/npc/npc-editor';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchNpc, fetchNpcVersion } from '@/lib/api';
import { useConsumedCopyFlag } from '@/lib/copy-search';
import { ficheTabOf, readNpcFicheSearch } from '@/lib/npc-search';
import { Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * La fiche d'un NPC existant (npc.md §3, §10, issue #210).
 *
 * The persona's route file states the four rules this one follows, and none differs - they belong
 * to the versioning pattern, not to the object:
 *
 *   - **two queries, two shapes, two key prefixes**: `['npc', email, id]` holds an `NpcDetail`,
 *     `['npc-version', email, id, versionId]` an `NpcVersion`, and neither overlaps `['npcs', email]`,
 *     which the library fills with the card projection;
 *   - **« Voir » and the tab travel in the URL** (`?version=`, `?onglet=rejeux`), because what
 *     somebody is reading is what gets pasted into a thread; `validateSearch` never throws;
 *   - **the remount key is the id plus a COUNTER**, never the draft's stamp: every autosave answers
 *     a new stamp into this very query, and a key built on it would remount the textarea under the
 *     caret once a second;
 *   - **the bump waits for the refetch to LAND**, because remounting before the fresh answer arrives
 *     would re-seed the editor from the stale draft it is escaping.
 *
 * The tab strip and the replays are the editor's, and the launch box hangs from its `asideTop`
 * (`NpcFicheAside`): this route reads the entity and hands it over. A 404 is « Ce NPC n'existe pas », which is also what a replay-closed
 * server answers: the section does not exist there.
 */

export const Route = createFileRoute('/admin/npc/$npcId')({
  validateSearch: readNpcFicheSearch,
  component: NpcFichePage,
});

function NpcFichePage() {
  const { email } = useAdminSession();
  const { npcId } = Route.useParams();
  const search = Route.useSearch();
  const { version, copie } = search;
  const tab = ficheTabOf(search);
  const navigate = useNavigate();
  const [reload, setReload] = useState(0);

  const npc = useQuery({
    queryKey: ['npc', email, npcId],
    queryFn: () => fetchNpc(npcId),
    retry: false,
  });
  const viewed = useQuery({
    queryKey: ['npc-version', email, npcId, version ?? null],
    queryFn: () => fetchNpcVersion(npcId, version ?? ''),
    enabled: version !== undefined,
    retry: false,
  });

  // The flag of a copy is consumed once the editor it describes has OPENED (§4.2): see the
  // persona's route for the two halves of that sentence.
  useConsumedCopyFlag(copie, npc.data !== undefined, () =>
    navigate({
      to: '/admin/npc/$npcId',
      params: { npcId },
      search: {
        ...(version === undefined ? {} : { version }),
        ...(tab === 'rejeux' ? { onglet: 'rejeux' as const } : {}),
      },
      replace: true,
    }),
  );

  const rejected = useRejectOn401(npc.error);
  const missing = npc.error instanceof ApiError && npc.error.status === 404;

  return (
    <div className="space-y-6">
      <p className="text-gray-600 text-small">
        <Link to="/admin/npc" className="text-bleu-nuit underline underline-offset-2">
          NPC
        </Link>
        <span className="mx-2">›</span>
        {npc.data?.name ?? '...'}
      </p>

      {npc.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement du NPC" />
        </div>
      ) : null}

      {missing ? (
        <p data-testid="npc-introuvable" className="text-gray-700">
          Ce NPC n’existe pas.{' '}
          <Link to="/admin/npc" className="text-bleu-nuit underline underline-offset-2">
            Revenir à la bibliothèque
          </Link>
          .
        </p>
      ) : null}

      {npc.isError && !missing && !rejected ? (
        <p className="text-gray-700">Ce NPC n’a pas pu être chargé.</p>
      ) : null}

      {npc.data === undefined ? null : (
        <NpcEditor
          key={`${npc.data.id}-${reload}`}
          selectName={copie === true}
          detail={npc.data}
          viewing={viewed.data ?? null}
          tab={tab}
          asideTop={
            <NpcFicheAside
              npcId={npc.data.id}
              name={npc.data.name}
              retired={npc.data.retiredAt !== null}
            />
          }
          onView={(summary) =>
            navigate({
              to: '/admin/npc/$npcId',
              params: { npcId },
              search: { version: summary.id },
            })
          }
          onCloseView={() => navigate({ to: '/admin/npc/$npcId', params: { npcId }, search: {} })}
          onReload={() => {
            void npc.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
